/**
 * Lenses: the query parameters that choose which rows of an event log a read serves.
 *
 * A lens is a `WHERE` on the log: one feed's items, one driver's events, events of these kinds. The one coverage rule
 * every lens has is top down: a read with no value for a lens holds every row, so it holds every narrower slice of
 * the same window. The reverse never holds, and slices are never added up to make a whole, so lenses need no further
 * distinction between them. What part of each row a read serves is a shape (`shape.ts`), not a lens.
 */

import { z } from "zod";
import type { ParamDefinition, ParamQuery, ParamValues } from "./params";

export type LensDefinition<TName extends string, TSchema extends z.ZodType> = ParamDefinition<TName, TSchema> & {
  /** Tells a lens from a shape in the type, so a spec cannot put one under the other. */
  role: "lens";
};

/**
 * Declares one lens, keeping its name as a literal so the types derived from a spec stay exact.
 */
export function defineLens<const TName extends string, TSchema extends z.ZodType>(
  name: TName,
  schema: TSchema,
  options: { description: string },
): LensDefinition<TName, TSchema> {
  return { name, schema, description: options.description, role: "lens" };
}

/**
 * A spec's lenses, keyed by name.
 */
export type LensRecord = Record<string, LensDefinition<string, z.ZodType>>;

/**
 * The lens values a handler holds once the route's schema has read them.
 */
export type LensValues<L extends LensRecord> = ParamValues<L>;

/**
 * The lens values a client sends: the wire shape, before the schema reads it.
 */
export type LensQuery<L extends LensRecord> = ParamQuery<L>;

/**
 * A list lens: comma-separated on the wire, an array of `item` in the handler, and absent when not sent.
 *
 * Blank entries are dropped, so a trailing comma is harmless; an empty list is refused rather than read as "all".
 */
export function listLens<T extends z.ZodType>(item: T) {
  const list = z.array(item).min(1);
  return z
    .string()
    .transform((raw, ctx): z.output<T>[] => {
      const entries = raw
        .split(",")
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);
      const parsed = list.safeParse(entries);
      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          ctx.addIssue({ code: "custom", message: issue.message, path: issue.path });
        }
        return z.NEVER;
      }
      return parsed.data;
    })
    .optional();
}
